// Shared building blocks of every /api/v1 response and query: ids, the value-display contract (Valued<T>),
// card footers, the common filter query, paging, the error envelope and export requests.
// Rule: the server sends numbers and keys, never sentences (i18n lives in the web).
import { z } from 'zod';
import {
  DisplayState,
  MISSING_REASONS,
  MissingReason,
  PartialReason,
  ReasonDetail,
  ReasonCategory,
  SupportItem,
  SurfaceFilter,
  Tool,
  TOOLS,
} from './enums.js';
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_MAX, PERIOD_RULES } from './controls.js';
import { RoleKind, ViewScope } from './roles-menus.js';

// ───────────────────────── Scalars ─────────────────────────

/** Opaque ids (users `u_…`, teams, roles, records…). Never shown on screen. */
export const Id = z.string().min(1).max(80);
export const UserId = Id;
export const TeamId = Id;
/** System role id or a custom role id. */
export const RoleId = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/);
/** UTC epoch milliseconds (stored as bigint, sent as a JSON number). */
export const EpochMs = z.int().nonnegative();
/** Calendar date in the organization display time zone (default Asia/Seoul). */
export const Ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Money is integer micro-dollars (plan CONVENTIONS 3). */
export const Micros = z.int();
export const Count = z.int().nonnegative();
/** Percentage 0–100 with one decimal. */
export const Pct = z.number().min(0).max(100);
/** Admin- or seed-authored text kept in both languages (notice sections, glossary). */
export const LocalizedText = z.object({ ko: z.string(), en: z.string() });
export type LocalizedText = z.infer<typeof LocalizedText>;

export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;
/** Empty request body / no parameters. */
export const NoBody = z.object({}).strict();

// ───────────────────────── Value-display contract (plan 8.5.2–8.5.4) ─────────────────────────

const valueMeta = {
  state: DisplayState,
  /** Required when state = 'none' — one of the 13 reason keys. */
  reason: MissingReason.optional(),
  /** Required when state = 'partial'. */
  partial: PartialReason.optional(),
  /** Tool-specific help key (web i18n `reasonDetail.<key>`). */
  detail: ReasonDetail.optional(),
  /** Support-table cell the badge links to (`#/connect?tab=support&cell=support-<tool>-<item>`). */
  tool: Tool.optional(),
  item: SupportItem.optional(),
};

/**
 * A value with its display state: ① value ② 0 (confirmed) ③ "no record · reason" ④ "partial · reason".
 * Invariants: value ⇒ value≠null · zero ⇒ value=0 · none ⇒ value=null + reason · partial ⇒ value≠null + partial.
 */
export function valued<T extends z.ZodType>(inner: T) {
  return z
    .object({ value: inner.nullable(), ...valueMeta })
    .superRefine((v, ctx) => {
      const val = (v as unknown as { value: unknown }).value;
      if (v.state === 'none') {
        if (val !== null) ctx.addIssue({ code: 'custom', message: "state 'none' must carry value null", path: ['value'] });
        if (!v.reason) ctx.addIssue({ code: 'custom', message: "state 'none' needs a reason", path: ['reason'] });
      } else if (val === null) {
        ctx.addIssue({ code: 'custom', message: `state '${v.state}' needs a value`, path: ['value'] });
      }
      if (v.state === 'zero' && val !== 0) ctx.addIssue({ code: 'custom', message: "state 'zero' must carry value 0", path: ['value'] });
      if (v.state === 'partial' && !v.partial) ctx.addIssue({ code: 'custom', message: "state 'partial' needs a partial reason", path: ['partial'] });
    });
}
export type Valued<T> = {
  value: T | null;
  state: DisplayState;
  reason?: MissingReason;
  partial?: PartialReason;
  detail?: ReasonDetail;
  tool?: Tool;
  item?: SupportItem;
};
export const ValuedNumber = valued(z.number());
export const ValuedInt = valued(z.int());
export const ValuedMicros = valued(Micros);
export type ValuedNumber = Valued<number>;

type Extra = { detail?: ReasonDetail; tool?: Tool; item?: SupportItem };
/** Constructors so the server never builds an invalid cell by hand. */
export const Val = {
  /** A received number: 0 becomes the confirmed-zero state. */
  of: (value: number, extra: Extra = {}): Valued<number> => (value === 0 ? { value: 0, state: 'zero', ...extra } : { value, state: 'value', ...extra }),
  zero: (extra: Extra = {}): Valued<number> => ({ value: 0, state: 'zero', ...extra }),
  none: <T = number>(reason: MissingReason, extra: Extra = {}): Valued<T> => ({ value: null, state: 'none', reason, ...extra }),
  partial: <T>(value: T, partial: PartialReason, extra: Extra = {}): Valued<T> => ({ value, state: 'partial', partial, ...extra }),
} as const;

/** "Excluded n people · n records (by reason)" for sums that skipped 'no record' cells (plan 8.5.1 ⑧, 9.1.6). */
export const ExcludedCounts = z.object({
  records: Count,
  people: Count,
  byReason: z.partialRecord(MissingReason, Count),
  /** Records hidden by a pending/done deletion request ("삭제 요청 n건 제외"). */
  deleted: Count,
  /** Manager team view: people outside the viewer's scope (other managers, admins, content reviewers). */
  outOfScopePeople: Count.optional(),
});
export type ExcludedCounts = z.infer<typeof ExcludedCounts>;
export const PartialCounts = z.object({ records: Count, byReason: z.partialRecord(PartialReason, Count) });
export type PartialCounts = z.infer<typeof PartialCounts>;

/**
 * Card footer facts (plan 8.5.1 ⑧, 10.4.1): basis n · excluded · condition (from the response's `applied`) · updated
 * time. The web renders the sentence and always appends "기록 없음 ≠ 안 씀".
 */
export const CardFooter = z.object({
  basis: Count,
  excluded: ExcludedCounts,
  partial: PartialCounts.optional(),
  /** When the previous period has no record the delta sentence is skipped and the footer names the reason. */
  prevMissing: MissingReason.optional(),
  asOf: EpochMs,
});
export type CardFooter = z.infer<typeof CardFooter>;
export const emptyExcluded = (): ExcludedCounts => ({ records: 0, people: 0, byReason: {}, deleted: 0 });

/** Change vs the previous equal-length period (plan 9.1.5, mockup D.delta). */
export const DELTA_DIRS = ['up', 'down', 'flat', 'new', 'none'] as const;
export const Delta = z.object({ dir: z.enum(DELTA_DIRS), pct: z.int().nonnegative().nullable() });
export type Delta = z.infer<typeof Delta>;
/** none: a side is missing · new: prev 0 → cur > 0 · flat: |Δ| < 1% · else up/down with rounded %. */
export function computeDelta(cur: number | null, prev: number | null): Delta {
  if (cur == null || prev == null) return { dir: 'none', pct: null };
  if (prev === 0) return cur === 0 ? { dir: 'flat', pct: 0 } : { dir: 'new', pct: null };
  const p = ((cur - prev) / prev) * 100;
  return { dir: Math.abs(p) < 1 ? 'flat' : p > 0 ? 'up' : 'down', pct: Math.round(Math.abs(p)) };
}
/** A KPI pair: the web builds "직전 7일보다 12% 늘었어요(214→240건)" from it. */
export const Comparison = z.object({ cur: ValuedNumber, prev: ValuedNumber, delta: Delta });
export type Comparison = z.infer<typeof Comparison>;

// ───────────────────────── People, teams, records as seen by the viewer ─────────────────────────

/**
 * A person as the viewer may see them. `name` is the real name only for self or inside the viewer's
 * `people.identity` scope; otherwise null and the web shows "사용자 #<pseudonym>" with a '#' avatar.
 */
export const PersonRef = z.object({
  id: UserId,
  name: z.string().nullable(),
  /** Stable 4-hex code of the pseudonym ("사용자 #1F3A"). */
  pseudonym: z.string().regex(/^[0-9A-F]{4}$/),
  self: z.boolean(),
  avatarHue: z.int().min(0).max(359),
});
export type PersonRef = z.infer<typeof PersonRef>;

/** A team; `parent` is the next level up (본부) for the "본부 › 팀" path. */
export const TeamRef = z.object({ id: TeamId, name: z.string(), parent: z.object({ id: TeamId, name: z.string() }).nullable() });
export type TeamRef = z.infer<typeof TeamRef>;

/** A role badge: system roles are named by the web (`role.<id>`), custom roles carry their own names. */
export const RoleRef = z.object({ id: RoleId, kind: RoleKind, name: LocalizedText.nullable() });
export type RoleRef = z.infer<typeof RoleRef>;

/** A conversation reference: opaque id + display number ("대화 #1330"). */
export const RecordRef = z.object({ id: Id, no: z.int().positive() });
export type RecordRef = z.infer<typeof RecordRef>;

// ───────────────────────── Common filter query (plan 10.3.5, mockup core.js parseQuery/periodToQuery) ─────────────────────────

export const PERIOD_PRESETS = ['today', '7d', '30d', '90d'] as const;
export const PeriodPreset = z.enum(PERIOD_PRESETS);
export type PeriodPreset = z.infer<typeof PeriodPreset>;
export const METHOD_FILTERS = ['all', 'cli', 'api'] as const;
export const MethodFilter = z.enum(METHOD_FILTERS);
export type MethodFilter = z.infer<typeof MethodFilter>;

const RANGE_RE = /^(\d{4}-\d{2}-\d{2})~(\d{4}-\d{2}-\d{2})$/;
const dayNo = (ymd: string): number => Math.floor(Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)) / 86_400_000);
const isRealDate = (ymd: string): boolean => {
  const d = new Date(Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)));
  return d.toISOString().slice(0, 10) === ymd;
};
/** Inclusive day count of a custom range. */
export const rangeDays = (from: string, to: string): number => dayNo(to) - dayNo(from) + 1;

/**
 * Problem with a custom range, or null. `today` (org time zone) is supplied by the caller so the pure package needs no
 * clock: no future date, from ≤ to, at most 366 days inclusive (plan 10.3.1, mockup rangeProblem).
 */
export function periodProblem(from: string, to: string, today?: string): 'format' | 'order' | 'future' | 'too_long' | null {
  if (!Ymd.safeParse(from).success || !Ymd.safeParse(to).success || !isRealDate(from) || !isRealDate(to)) return 'format';
  if (from > to) return 'order';
  if (today && to > today) return 'future';
  if (rangeDays(from, to) > PERIOD_RULES.customMaxDays) return 'too_long';
  return null;
}

/** `period` accepts a preset, `from~to`, or `custom` together with `from`/`to` (alias of plan 10.3.5). */
export const PeriodParam = z.union([PeriodPreset, z.literal('custom'), z.string().regex(RANGE_RE)]);
export type PeriodParam = z.infer<typeof PeriodParam>;

export type ParsedPeriod = { key: PeriodPreset } | { key: 'custom'; from: string; to: string };
/** Canonical form of the period params (null when unusable). */
export function parsePeriod(q: { period?: string; from?: string; to?: string }, today?: string): ParsedPeriod | null {
  const p = q.period ?? '7d';
  if ((PERIOD_PRESETS as readonly string[]).includes(p)) return { key: p as PeriodPreset };
  let from: string | undefined;
  let to: string | undefined;
  if (p === 'custom') ({ from, to } = q);
  else {
    const m = RANGE_RE.exec(p);
    if (m) [from, to] = [m[1], m[2]];
  }
  if (!from || !to || periodProblem(from, to, today)) return null;
  return { key: 'custom', from, to };
}
/** Inverse of parsePeriod for URLs (mockup periodToQuery): presets stay, custom becomes `from~to`. */
export const periodToQuery = (p: ParsedPeriod): string => (p.key === 'custom' ? `${p.from}~${p.to}` : p.key);

/** Comma-separated list in a query string → array of items (max 20). */
export const csvOf = <T extends z.ZodType<string, string>>(item: T) =>
  z
    .string()
    .max(2000)
    .transform((s) => s.split(',').map((x) => x.trim()).filter(Boolean))
    .pipe(z.array(item).max(20));

/**
 * The one common filter every list/aggregate endpoint accepts: view scope · period · method · team · person.
 * Unknown keys are ignored (plan 10.3.5); wrong values are 400. Exceeding the caller's scope is 403 (never widened).
 * `team` is only meaningful with scope=all (team filter inside "전체"); `user` narrows to one person within scope.
 */
export const commonFilterShape = {
  scope: ViewScope.default('me'),
  period: PeriodParam.default('7d'),
  from: Ymd.optional(),
  to: Ymd.optional(),
  method: MethodFilter.default('all'),
  team: csvOf(TeamId).optional(),
  user: UserId.optional(),
};
const periodCheck = (input: unknown, ctx: z.RefinementCtx): void => {
  const q = input as { period: string; from?: string; to?: string };
  if (q.period === 'custom' && (!q.from || !q.to)) ctx.addIssue({ code: 'custom', message: 'period=custom needs from and to', path: ['period'] });
  else if (!parsePeriod(q)) ctx.addIssue({ code: 'custom', message: 'invalid period range (format, order or > 366 days)', path: ['period'] });
};
/** Build an endpoint query = common filter + extra keys, with the period rules applied once. */
export const filterQuery = <S extends z.ZodRawShape>(extra: S) => z.object({ ...commonFilterShape, ...extra }).superRefine(periodCheck);
/** Period-only query (admin screens with a period toolbar but no view scope: audit, collection status). */
export const periodQuery = <S extends z.ZodRawShape>(extra: S) =>
  z.object({ period: commonFilterShape.period, from: commonFilterShape.from, to: commonFilterShape.to, ...extra }).superRefine(periodCheck);
export const CommonFilterQuery = filterQuery({});
export type CommonFilterQuery = z.infer<typeof CommonFilterQuery>;
export type CommonFilterInput = z.input<typeof CommonFilterQuery>;

/** Extra record/stat narrowing used by SCR-03 and SCR-05 (`tool`, `model`, `surface`). */
export const ToolFilter = csvOf(Tool);
export const ModelFilter = csvOf(z.string().min(1).max(80));

/** The normalized period the server actually used (dates in the display time zone, [fromTs, toTs)). */
export const AppliedPeriod = z.object({
  key: z.enum([...PERIOD_PRESETS, 'custom']),
  from: Ymd,
  to: Ymd,
  fromTs: EpochMs,
  toTs: EpochMs,
  /** Previous equal-length window (today → yesterday up to the same time). */
  prevFromTs: EpochMs,
  prevToTs: EpochMs,
  days: z.int().positive(),
  tz: z.string(),
});
export type AppliedPeriod = z.infer<typeof AppliedPeriod>;

/** Echo of the filter after normalization; footers build "조건: 30일, CLI 도구, 플랫폼팀" from it. */
export const AppliedFilter = z.object({
  scope: ViewScope,
  period: AppliedPeriod,
  method: MethodFilter,
  teams: z.array(TeamRef),
  user: PersonRef.nullable(),
  tools: z.array(Tool),
  /** Model filter echo with display labels (chips and footers print model names). */
  models: z.array(z.object({ id: z.string(), label: z.string() })),
});
export type AppliedFilter = z.infer<typeof AppliedFilter>;

// ───────────────────────── Paging ─────────────────────────

export const PageQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
export const PageMeta = z.object({ page: z.int().min(1), pageSize: z.int().min(1), total: Count, pages: Count });
export type PageMeta = z.infer<typeof PageMeta>;
export const paged = <T extends z.ZodType>(item: T) => z.object({ items: z.array(item), page: PageMeta });

/** Cursor paging for append-only streams (audit log, notifications). */
export const CursorQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const cursored = <T extends z.ZodType>(item: T) => z.object({ items: z.array(item), nextCursor: z.string().nullable() });

// ───────────────────────── Errors ─────────────────────────

/** Error codes and their HTTP status. Schema failures are 400 `validation_failed`; rule violations 422. */
export const ERROR_STATUS = {
  bad_request: 400,
  validation_failed: 400,
  period_invalid: 400,
  option_invalid: 400,
  unauthenticated: 401,
  session_expired: 401,
  invalid_credentials: 401,
  invalid_code: 401,
  token_invalid: 401,
  forbidden: 403,
  csrf_failed: 403,
  origin_rejected: 403,
  scope_exceeds_permission: 403,
  password_change_required: 403,
  policy_ack_required: 403,
  mfa_enroll_required: 403,
  reauth_required: 403,
  self_grant: 403,
  permission_not_subset: 403,
  scope_exceeds_grantor: 403,
  target_more_privileged: 403,
  requester_cannot_approve: 403,
  role_system_readonly: 403,
  person_comparison_forbidden: 403,
  person_metric_blocked: 403,
  value_sort_disabled: 403,
  gateway_consent_blocked: 403,
  not_found: 404,
  conflict: 409,
  stale_version: 409,
  last_admin: 409,
  grant_duplicate: 409,
  grant_not_needed: 409,
  approval_pending: 409,
  undo_expired: 409,
  already_invited: 409,
  role_in_use: 409,
  name_duplicate: 409,
  notice_changed: 409,
  legal_hold_active: 409,
  already_processed: 409,
  /** PUT /admin/policy touched a value locked by the environment (PolicyView.locks, plan CK-P3-16). details.paths. */
  setting_locked: 409,
  /** A deletion request blocks the record for every role (plan 8.4.4, 11.9): details = RecordDeletedDetails. */
  record_deletion_requested: 410,
  payload_too_large: 413,
  unsupported_media_type: 415,
  reason_too_short: 422,
  reason_category_required: 422,
  no_content_stored: 422,
  expiry_required: 422,
  expiry_invalid: 422,
  teams_required: 422,
  password_policy: 422,
  memo_too_short: 422,
  token_limit_reached: 422,
  export_row_limit: 422,
  setup_code_invalid: 422,
  domain_not_allowed: 422,
  /** Work details / tool output turned on without the risk confirmation (plan 11.3.1 ④⑤). details.ack. */
  risk_ack_required: 422,
  /** A requested recording item exceeds the current company cap (plan 11.3.1). */
  consent_cap_exceeded: 422,
  setup_not_available: 409,
  rate_limited: 429,
  account_locked: 429,
  internal_error: 500,
  gateway_disabled: 503,
  service_unavailable: 503,
} as const;
export type ErrorCode = keyof typeof ERROR_STATUS;
export const ERROR_CODES = Object.keys(ERROR_STATUS) as [ErrorCode, ...ErrorCode[]];
export const ErrorCodeSchema = z.enum(ERROR_CODES);

/**
 * `{ error: { code, message, requestId, details? } }`. `message` is a short developer-facing English line (never shown,
 * never contains text bodies or secrets); the web words the error from `code` + `details` and shows `requestId`.
 */
export const ErrorEnvelope = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string().max(500),
    requestId: z.string().min(1).max(100),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;

// ───────────────────────── Export requests (plan 11.10) ─────────────────────────

export const EXPORT_KINDS = ['aggregate', 'rows', 'identity', 'content'] as const;
export const ExportKind = z.enum(EXPORT_KINDS);
export type ExportKind = z.infer<typeof ExportKind>;
export const EXPORT_FORMATS = ['csv', 'json'] as const;
export const ExportFormat = z.enum(EXPORT_FORMATS);
export type ExportFormat = z.infer<typeof ExportFormat>;
export const EXPORT_SOURCES = ['stats_card', 'records', 'people', 'audit', 'my_copy'] as const;
export const ExportSource = z.enum(EXPORT_SOURCES);
export type ExportSource = z.infer<typeof ExportSource>;

/**
 * Body of every export endpoint. aggregate/rows need no reason; identity/content need a reason category + reason
 * (≥ policy minimum) and the pledge (plan 11.10). The server re-checks `export.<kind>` at the requested scope.
 */
export const ExportRequest = z
  .object({
    kind: ExportKind,
    format: ExportFormat,
    filter: CommonFilterQuery,
    reasonCategory: ReasonCategory.optional(),
    reason: z.string().trim().max(200).optional(),
  })
  .superRefine((b, ctx) => {
    if ((b.kind === 'identity' || b.kind === 'content') && (!b.reasonCategory || !b.reason || b.reason.length < 10))
      ctx.addIssue({ code: 'custom', message: 'identity/content export needs reasonCategory and a reason (≥ 10 chars)', path: ['reason'] });
  });
export type ExportRequest = z.infer<typeof ExportRequest>;

/**
 * Header block of an export file: CSV `# key: value` lines before the table, JSON `meta`. Files are streamed with
 * `Cache-Control: no-store`, `X-Content-Type-Options: nosniff`, never kept on the server; only `exports` keeps the row.
 */
export const ExportMeta = z.object({
  exportId: z.string(),
  createdAt: EpochMs,
  actorRole: RoleId,
  kind: ExportKind,
  source: ExportSource,
  applied: AppliedFilter,
  rowCount: Count,
  rowLimit: z.int().positive(),
  truncated: z.boolean(),
  /** Metric dictionary version (stats exports). */
  dictionaryVersion: z.string().optional(),
  /** Fixed notice keys printed in the header (prohibited use, redaction limits). */
  notices: z.array(z.enum(['prohibited_use', 'redaction_limit', 'small_group_mark'])),
  /** Human-readable notices travel with the file, including JSON downloads. */
  noticeText: z.object({ prohibitedUse: z.string(), redactionLimit: z.string() }).optional(),
  /** Extra record selectors not represented by AppliedFilter (never persisted as plaintext in export receipts). */
  recordFilter: z.object({ surface: SurfaceFilter.optional(), q: z.string().max(300).optional() }).optional(),
  /** The own-copy selector is independent of other export permissions and has no reason. */
  copyFilter: z.object({ period: z.enum(['30d', '90d', 'all']), withText: z.boolean(), scope: z.literal('me') }).optional(),
});
export type ExportMeta = z.infer<typeof ExportMeta>;
/** JSON export file. CSV files carry the same meta as comment lines, UTF-8 BOM, formula-guarded cells. */
export const ExportJsonFile = z.object({ meta: ExportMeta, columns: z.array(z.string()), rows: z.array(z.array(z.union([z.string(), z.number(), z.null()]))) });
export type ExportJsonFile = z.infer<typeof ExportJsonFile>;

/**
 * Undo window after an applied change (plan 8.4.11 "저장하면 되돌리기 알림(남은 시간 표시)"; mockup snackbar). The change
 * is applied at once; POST /api/v1/undo/:token before `until` restores the exact prior state (no new approval round)
 * and is logged as `change.undo`. Side effects that disturb people (session revocation, notifications) wait until
 * `until` passes. After that: 409 `undo_expired`.
 */
export const UndoTicket = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{16,80}$/), until: EpochMs });
export type UndoTicket = z.infer<typeof UndoTicket>;
export const UndoParams = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{16,80}$/) });
/**
 * `action` = the audit action code that was undone (role.create, role.change, role.delete, role.grant, role.revoke,
 * approval.withdraw, case.close, sa.suspend, sa.resume).
 */
export const UndoResponse = z.object({ undone: z.literal(true), action: z.string() });

/** Long-running job handle (glossary link check, org-report sync, retention run). */
export const JOB_HANDLE_STATES = ['queued', 'running', 'done', 'failed'] as const;
export const JobHandle = z.object({
  jobId: Id,
  state: z.enum(JOB_HANDLE_STATES),
  progress: z.object({ done: Count, total: Count }).nullable(),
  startedAt: EpochMs.nullable(),
  finishedAt: EpochMs.nullable(),
  /** Error code when failed (no free text). */
  errorCode: z.string().nullable(),
});
export type JobHandle = z.infer<typeof JobHandle>;

/** Re-exported for convenience: the 13 reason keys in plan order. */
export const REASON_KEYS = MISSING_REASONS;
export const TOOL_IDS = TOOLS;
